import type { ExtractedDataItem } from '../types';

/**
 * A field's identity across a live update: its key on its page. The page is
 * `page_number` (repeating templates) or the model's `source_page` — one
 * template read on two pages emits the same key on both, and keying on
 * `page_number` alone copied page 1's accepted value onto page 2.
 */
const identity = (f: ExtractedDataItem) =>
  `${f.key}::${f.page_number ?? f.source_page ?? ''}`;

/**
 * Lay a live extraction over the cached one without undoing the operator's
 * work: a field already accepted keeps its accepted value and status.
 */
export function mergeAcceptedFields(
  existing: ExtractedDataItem[] | null | undefined,
  incoming: ExtractedDataItem[]
): ExtractedDataItem[] {
  if (!existing || existing.length === 0) return incoming;

  const acceptedById = new Map<string, ExtractedDataItem>();
  for (const f of existing) {
    if (f.status === 'processed') acceptedById.set(identity(f), f);
  }
  if (acceptedById.size === 0) return incoming;

  return incoming.map((item) => {
    const prior = acceptedById.get(identity(item));
    return prior
      ? { ...item, status: 'processed' as const, value: prior.value }
      : item;
  });
}
